import { useEffect, useRef, useState } from "react";
import { Copy, Eye, EyeOff, RefreshCw, Webhook, Code2, Search as SearchIcon, LayoutTemplate, Download, Package, Palette } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { GuaranteeSeal } from "@/components/GuaranteeSeal";
import { BadgeSettings } from "@/components/admin/BadgeSettings";
import { Button } from "@/components/ui/button";

const Code = ({ code, id }) => (
  <div className="relative group">
    <pre className="bg-[#0A0A0A] text-emerald-100 text-xs leading-relaxed rounded-md p-4 pr-14 overflow-x-auto" data-testid={`code-${id}`}><code>{code}</code></pre>
    <Button size="icon" variant="secondary" className="absolute top-2 right-2 h-8 w-8" onClick={() => { navigator.clipboard.writeText(code); toast.success("Copiato negli appunti"); }} data-testid={`copy-${id}`}><Copy size={14} /></Button>
  </div>
);

const Section = ({ icon: Icon, title, sub, children }) => (
  <section className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-4"><span className="h-9 w-9 rounded-md bg-emerald-900/5 text-emerald-900 flex items-center justify-center"><Icon size={18} /></span>
      <div><h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>{sub && <p className="text-sm text-gray-600">{sub}</p>}</div></div>
    {children}
  </section>
);

const php = (base) => `<?php
// Rocard Recensioni — incolla in functions.php del tema child o con il plugin "Code Snippets"
define('ROCARD_REVIEWS_URL', '${base}');

// 1) Badge flottante + widget su tutto il sito
add_action('wp_footer', function () {
  echo '<script src="' . ROCARD_REVIEWS_URL . '/api/widget.js" data-badge="true" data-position="left" async></script>';
});

// 2) Stelle sotto il titolo del prodotto
add_action('woocommerce_single_product_summary', function () {
  global $product;
  echo '<div class="rocard-reviews" data-type="stars" data-hide-empty="true" data-product-id="' . esc_attr($product->get_id()) . '"></div>';
}, 6);

// 3) Elenco recensioni prodotto sotto la scheda
add_action('woocommerce_after_single_product_summary', function () {
  global $product;
  echo '<div class="rocard-reviews" data-type="product" data-product-id="' . esc_attr($product->get_id()) . '" style="margin:32px 0"></div>';
}, 12);

// 4) Rich snippet Google: aggiunge voto e recensioni ai dati strutturati di WooCommerce
add_filter('woocommerce_structured_data_product', function ($markup, $product) {
  $id = $product->get_id();
  $key = 'rocard_jsonld_' . $id;
  $data = get_transient($key);
  if ($data === false) {
    $res = wp_remote_get(ROCARD_REVIEWS_URL . '/api/public/jsonld?product_id=' . $id, ['timeout' => 3]);
    $data = is_wp_error($res) ? null : json_decode(wp_remote_retrieve_body($res), true);
    set_transient($key, $data ?: [], HOUR_IN_SECONDS);
  }
  if (!empty($data['aggregateRating'])) {
    $markup['aggregateRating'] = $data['aggregateRating'];
    $markup['review'] = $data['review'];
  }
  return $markup;
}, 10, 2);`;

const LivePreview = ({ base }) => {
  const ref = useRef(null);
  useEffect(() => {
    const s = document.createElement("script");
    s.src = `${base}/api/widget.js?preview=${Date.now()}`;
    s.async = true;
    ref.current?.appendChild(s);
  }, [base]);
  return (
    <div ref={ref} className="space-y-6" data-testid="widget-live-preview">
      <div className="border border-dashed border-gray-300 rounded-md p-4"><p className="text-xs text-gray-500 mb-2">Stelle (inline)</p><div className="rocard-reviews" data-type="stars" /></div>
      <div className="border border-dashed border-gray-300 rounded-md p-4"><p className="text-xs text-gray-500 mb-2">Carosello recensioni negozio</p><div className="rocard-reviews" data-type="carousel" /><p className="text-xs text-gray-400">Il carosello appare quando ci sono recensioni pubblicate.</p></div>
      <div className="border border-dashed border-gray-300 rounded-md p-4 flex items-center gap-4"><p className="text-xs text-gray-500">Badge flottante (angolo pagina)</p><div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-[0_6px_24px_rgba(17,24,39,.12)]"><GuaranteeSeal /></div></div>
    </div>
  );
};

export default function Integrations() {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [secret, setSecret] = useState("");
  const [show, setShow] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setSecret(r.data.webhook_secret)); }, []);
  const regen = async () => {
    if (!window.confirm("Rigenerare il segreto? Dovrai aggiornarlo anche in WooCommerce.")) return;
    const { data } = await api.post("/admin/settings/webhook-secret"); setSecret(data.webhook_secret); toast.success("Nuovo segreto generato");
  };
  return (
    <div className="fade-up space-y-6 max-w-5xl">
      <PageHeader title="Integrazioni" sub="Collega WooCommerce e mostra recensioni e garanzia su rocard.it" />
      <Section icon={Package} title="Plugin WordPress (consigliato)" sub="Un file da installare: badge, stelle, recensioni prodotto, shortcode e rich snippet Google">
        <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1 mb-4">
          <li>Scarica il plugin e la documentazione PDF e inviali al tuo sviluppatore</li>
          <li>WordPress → Plugin → Aggiungi nuovo → Carica plugin → Installa → Attiva</li>
          <li>Impostazioni → Rocard Recensioni: l'URL del servizio è già compilato</li>
          <li>Configura il webhook (punto 1 qui sotto)</li>
        </ol>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/admin/plugin.zip`} data-testid="download-plugin-button"><Button className="bg-emerald-900 hover:bg-emerald-800"><Download size={16} /> Scarica plugin (.zip)</Button></a>
          <a href={`${API}/admin/docs.pdf`} data-testid="download-docs-button"><Button variant="outline"><Download size={16} /> Documentazione PDF</Button></a>
        </div>
        <p className="text-xs text-gray-500 mt-3">Dopo il Deploy scarica di nuovo il plugin dal pannello pubblicato: conterrà l'indirizzo definitivo.</p>
      </Section>
      <Section icon={Webhook} title="1. Webhook WooCommerce" sub="Crea automaticamente un invito per ogni ordine completato">
        <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1 mb-4">
          <li>WordPress → WooCommerce → Impostazioni → Avanzate → Webhook → Aggiungi webhook</li>
          <li>Stato: <b>Attivo</b> · Argomento: <b>Ordine aggiornato</b> · Versione API: <b>WP REST API v3</b></li>
          <li>Incolla URL di consegna e segreto qui sotto, poi salva</li>
        </ol>
        <p className="text-xs font-semibold text-gray-500 mb-1">URL di consegna</p><Code id="webhook-url" code={`${API}/webhooks/woocommerce`} />
        <p className="text-xs font-semibold text-gray-500 mt-4 mb-1">Segreto</p>
        <div className="flex gap-2 items-center">
          <code className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs font-mono break-all" data-testid="webhook-secret-value">{show ? secret : "•".repeat(32)}</code>
          <Button size="icon" variant="outline" onClick={() => setShow(!show)} data-testid="webhook-secret-toggle">{show ? <EyeOff size={15} /> : <Eye size={15} />}</Button>
          <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(secret); toast.success("Segreto copiato"); }} data-testid="webhook-secret-copy"><Copy size={15} /></Button>
          <Button size="icon" variant="outline" onClick={regen} data-testid="webhook-secret-regenerate"><RefreshCw size={15} /></Button>
        </div>
      </Section>
      <Section icon={Code2} title="2. In alternativa al plugin: snippet PHP" sub="Stesse funzioni del plugin, da incollare con Code Snippets o nel tema child">
        <Code id="php-snippet" code={php(base)} />
        <p className="text-xs text-gray-500 mt-2">Consiglio: disattiva le recensioni native di WooCommerce (Impostazioni → Prodotti) per evitare doppioni.</p>
      </Section>
      <Section icon={LayoutTemplate} title="3. Widget HTML" sub="Per pagine, footer o page builder (Elementor, Gutenberg → blocco HTML)">
        <div className="grid md:grid-cols-2 gap-4">
          <div><p className="text-xs font-semibold text-gray-500 mb-1">Script (una volta per pagina)</p><Code id="script-tag" code={`<script src="${base}/api/widget.js" data-badge="true" async></script>`} /></div>
          <div><p className="text-xs font-semibold text-gray-500 mb-1">Stelle negozio</p><Code id="stars-tag" code={`<div class="rocard-reviews" data-type="stars"></div>`} /></div>
          <div><p className="text-xs font-semibold text-gray-500 mb-1">Carosello recensioni</p><Code id="carousel-tag" code={`<div class="rocard-reviews" data-type="carousel"></div>`} /></div>
          <div><p className="text-xs font-semibold text-gray-500 mb-1">Recensioni di un prodotto</p><Code id="product-tag" code={`<div class="rocard-reviews" data-type="product" data-product-id="123"></div>`} /></div>
        </div>
      </Section>
      <Section icon={SearchIcon} title="4. Google — stelline nei risultati" sub="Rich snippet tramite dati strutturati schema.org">
        <p className="text-sm text-gray-700">Lo snippet del punto 2 aggiunge <code>aggregateRating</code> e <code>review</code> ai dati strutturati Product che WooCommerce già genera. Verifica con lo strumento "Test dei risultati multimediali" di Google dopo l'installazione. Google decide in autonomia se e quando mostrare le stelline.</p>
        <p className="text-xs font-semibold text-gray-500 mt-4 mb-1">Esempio endpoint dati strutturati</p><Code id="jsonld-url" code={`${API}/public/jsonld?product_id=123`} />
      </Section>
      <Section icon={Palette} title="Badge personalizzato" sub="Colori, testi e posizione del badge Garanzia mostrato su rocard.it"><BadgeSettings /></Section>
      <Section icon={Eye} title="Anteprima live" sub="Dati reali dalle recensioni pubblicate"><LivePreview base={base} /></Section>
    </div>
  );
}
